import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/lib/api'

type DashResp = {
  kpi: Record<string, number>
  trends: Record<string, number>
}

export default function AdminDashboardPage() {
  const { data, isLoading, error } = useQuery<DashResp>({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => (await apiClient.get<DashResp>('/api/v1/admin/dashboard')).data,
  })

  if (isLoading) return <p className="text-sm text-neutral-500">Loading…</p>
  if (error || !data) return <p className="text-sm text-red-600">Failed to load</p>

  const kpi = [
    ['Users', 'totalUsers'],
    ['Workspaces', 'totalWorkspaces'],
    ['Skills', 'totalSkills'],
    ['Certified skills', 'certifiedSkills'],
    ['Listings', 'totalListings'],
    ['Forks', 'totalForks'],
    ['Reviews', 'totalReviews'],
    ['Disabled users', 'disabledUsers'],
    ['Pending data requests', 'pendingDataRequests'],
  ] as const

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Platform admin</h1>
        <p className="text-sm text-neutral-500">SUPERADMIN dashboard</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {kpi.map(([label, k]) => (
          <div key={k} className="rounded border bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-neutral-500">{label}</p>
            <p className="text-2xl font-semibold mt-1">
              {(data.kpi[k] ?? 0).toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded border bg-white p-4">
        <h2 className="font-semibold">30-day trends</h2>
        <dl className="grid grid-cols-2 gap-4 mt-2 text-sm">
          <div>
            <dt className="text-neutral-500">New users</dt>
            <dd className="font-semibold">{data.trends.newUsersLast30Days}</dd>
          </div>
          <div>
            <dt className="text-neutral-500">New skills</dt>
            <dd className="font-semibold">{data.trends.newSkillsLast30Days}</dd>
          </div>
        </dl>
      </div>

      <div className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Sections</h2>
        <ul className="grid grid-cols-2 gap-2 text-sm">
          <li><Link to="/admin/users" className="text-neutral-900 underline">Users</Link></li>
          <li><Link to="/admin/workspaces" className="text-neutral-900 underline">Workspaces</Link></li>
          <li><Link to="/admin/audit" className="text-neutral-900 underline">Audit log</Link></li>
        </ul>
      </div>
    </div>
  )
}
